import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import lockupHorizontalLight from "@adva/brand-assets/adva/lockup-horizontal/lockup-horizontal-light.svg";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";

export default function AcceptInvitePage() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenVerified, setTokenVerified] = useState(false);

  const token = searchParams.get("token");
  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type");

  useEffect(() => {
    if (session && tokenVerified) {
      navigate("/dashboard", { replace: true });
    }
  }, [session, tokenVerified, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0D10]">
        <Loader2 className="h-8 w-8 animate-spin text-[#7AA7D9]" />
      </div>
    );
  }

  if (session && !tokenVerified) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  if (!token && !tokenHash) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0B0D10] px-6 text-[#F5F3EE]">
        <img
          src={lockupHorizontalLight}
          alt="Adva"
          height={32}
          className="mb-8 h-8"
        />
        <h1 className="text-xl font-semibold">Invalid Invitation Link</h1>
        <p className="mt-2 text-[#F5F3EE]/60">
          This invitation link is missing or has expired. Please ask your
          administrator to resend the invitation.
        </p>
        <Link
          to="/login"
          className="mt-6 rounded-md bg-[#7AA7D9] px-6 py-2.5 text-sm font-medium text-[#0B0D10] hover:bg-[#8BB5E3]"
        >
          Go to Sign In
        </Link>
      </div>
    );
  }

  async function handleAccept(e: FormEvent) {
    e.preventDefault();
    if (!tosAccepted || !privacyAccepted) return;

    setVerifying(true);
    setError(null);

    try {
      const verifyParams: { token_hash: string; type: string } = {
        token_hash: tokenHash ?? token ?? "",
        type: type ?? "invite",
      };

      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: verifyParams.token_hash,
        type: verifyParams.type as "invite" | "magiclink" | "email",
      });

      if (verifyError) {
        setError(
          verifyError.message.includes("expired")
            ? "This invitation has expired. Please ask your administrator to resend it."
            : verifyError.message,
        );
        setVerifying(false);
        return;
      }

      setTokenVerified(true);
    } catch {
      setError("Something went wrong. Please try again.");
      setVerifying(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0B0D10] px-6 text-[#F5F3EE]">
      <div className="w-full max-w-md">
        <img
          src={lockupHorizontalLight}
          alt="Adva"
          height={32}
          className="mx-auto mb-10 h-8"
        />

        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-8">
          <h1 className="text-xl font-semibold">Accept Invitation</h1>
          <p className="mt-2 text-sm text-[#F5F3EE]/60">
            You&rsquo;ve been invited to join a team on Adva. Review and accept
            the terms below to continue.
          </p>

          <form onSubmit={handleAccept} className="mt-6 space-y-4">
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-[#7AA7D9] focus:ring-[#7AA7D9]"
              />
              <span className="text-[#F5F3EE]/80">
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7AA7D9] underline"
                >
                  Terms of Service
                </Link>
              </span>
            </label>

            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-[#7AA7D9] focus:ring-[#7AA7D9]"
              />
              <span className="text-[#F5F3EE]/80">
                I acknowledge the{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7AA7D9] underline"
                >
                  Privacy Policy
                </Link>
              </span>
            </label>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={!tosAccepted || !privacyAccepted || verifying}
              className="w-full rounded-md bg-[#7AA7D9] py-2.5 text-sm font-medium text-[#0B0D10] transition-colors hover:bg-[#8BB5E3] disabled:opacity-50"
            >
              {verifying ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Accept Invitation"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
